/** Papel y orientación de la plantilla, con el tamaño libre cuando es personalizado. */
import { CheckboxField } from '@/components/ds/CheckboxField'
import { SelectField } from '@/components/ds/SelectField'
import { TextField } from '@/components/ds/TextField'
import { PAPELES, admiteDosPorHoja, esRollo, type Papel } from './geometria'

export function ControlesDePapel({ papel, onChange, disabled = false, compacto = false }: {
  papel: Papel
  onChange: (p: Papel) => void
  disabled?: boolean
  /** En la barra del lienzo: en fila y sin ayudas. */
  compacto?: boolean
}) {
  const rollo = esRollo(papel.preset)
  const personalizado = papel.preset === 'custom'
  const dosPorHoja = !!papel.two_per_sheet && admiteDosPorHoja(papel)
  // Un papel que ya no admite dos por hoja (un rollo) lo pierde al cambiar.
  const conDos = (p: Papel): Papel => (dosPorHoja && admiteDosPorHoja(p) ? { ...p, two_per_sheet: true } : p)
  return (
    <div className={compacto ? 'flex flex-wrap items-end gap-2' : 'grid grid-cols-2 gap-3'}>
      <SelectField dense label="Papel" value={papel.preset} disabled={disabled} className={compacto ? 'w-44' : ''}
        onChange={e => {
          const preset = e.target.value
          onChange(conDos(preset === 'custom'
            ? { preset, width_mm: papel.width_mm ?? 80, height_mm: papel.height_mm ?? null }
            : { preset, ...(esRollo(preset) ? {} : { orientation: papel.orientation ?? 'portrait' }) }))
        }}>
        {PAPELES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
      </SelectField>
      <SelectField dense label="Orientación" value={rollo ? 'portrait' : papel.orientation ?? 'portrait'}
        disabled={disabled || rollo || (personalizado && papel.height_mm == null)} className={compacto ? 'w-36' : ''}
        hint={!compacto && rollo ? 'Un rollo no tiene orientación.' : undefined}
        onChange={e => onChange({ ...papel, orientation: e.target.value as Papel['orientation'] })}>
        <option value="portrait">Vertical</option>
        <option value="landscape">Horizontal</option>
      </SelectField>
      {personalizado && (
        <>
          <TextField dense label="Ancho (mm)" inputProps={{ type: 'number' }} value={papel.width_mm ?? ''} disabled={disabled}
            className={compacto ? 'w-28' : ''}
            onChange={e => onChange({ ...papel, width_mm: Number(e.target.value) })} />
          <TextField dense label="Alto (mm)" inputProps={{ type: 'number' }} value={papel.height_mm ?? ''} disabled={disabled}
            className={compacto ? 'w-28' : ''} placeholder="Automático"
            hint={compacto ? undefined : 'Vacío: se ajusta al contenido, como un rollo.'}
            onChange={e => onChange(conDos({ ...papel, height_mm: e.target.value === '' ? null : Number(e.target.value) }))} />
        </>
      )}
      {admiteDosPorHoja(papel) && (
        <CheckboxField label="Dos por hoja" checked={dosPorHoja} disabled={disabled}
          description={compacto ? undefined : 'La página se imprime dos veces en una hoja del doble de alto: media carta en una carta, para cortar.'}
          onChange={e => {
            const resto = { ...papel }
            delete resto.two_per_sheet
            onChange(e.target.checked ? { ...resto, two_per_sheet: true } : resto)
          }} />
      )}
    </div>
  )
}
